/**
 * Connections · Outbound webhooks (`/services/webhooks`): where YAGNI posts
 * channel, ticket and review events to the customer's own services. One row
 * per subscription; the sheet holds its events, filter and recent deliveries,
 * and the footer regenerates the signing secret or pauses it. Create webhook
 * opens the form in the sheet, and the signing secret shows there once.
 *
 * Inbound webhooks (from a connected tool into YAGNI) are configured on that
 * connection's sheet, not here.
 */
import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { Btn, Chip, Fold, Group, Groups, MenuItem, RecordHead, Row, SheetFoot, Stamp } from "../queue/index.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { fetchWorkspaceAgentIdentity } from "../lib/agentIdentity.js";
import { DeliveryInspector } from "../pages/Webhooks/DeliveryInspector.js";
import { SubscriptionForm } from "../pages/Webhooks/SubscriptionForm.js";
import type {
  AgentOption,
  ChannelOption,
  WebhookDeliveryStatus,
  WebhookSubscriptionListItem,
} from "../pages/Webhooks/types.js";
import type { ConnectionsTabProps } from "./ConnectionsPage.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; subscriptions: WebhookSubscriptionListItem[] }
  | { kind: "error"; error: string };

const MARK_STYLE = { "--c": "var(--agent-qa)" } as CSSProperties;

const STATUS_LABEL: Record<WebhookDeliveryStatus, string> = {
  pending: "pending",
  in_flight: "in flight",
  succeeded: "last delivered",
  failed: "last failed",
  dead: "last gave up",
};

/** The row's state line: where it posts, what, and how the last day went. */
export function webhookStateLine(sub: WebhookSubscriptionListItem): string {
  const host = sub.target_url.replace(/^https?:\/\//, "");
  const kinds = sub.event_kinds.join(", ");
  const count = sub.deliveries_24h_count ?? 0;
  const last = sub.last_delivery_status ? `, ${STATUS_LABEL[sub.last_delivery_status]}` : "";
  return `${host} · ${kinds} · ${count} in 24h${last}`;
}

function filterLine(sub: WebhookSubscriptionListItem, channels: ChannelOption[], agents: AgentOption[]): string {
  const name = (list: { id: string; name: string }[], id: string) => list.find((entry) => entry.id === id)?.name ?? id;
  const where = sub.channel_ids.length ? sub.channel_ids.map((id) => name(channels, id)).join(", ") : "every channel";
  const who = sub.agent_ids.length ? sub.agent_ids.map((id) => name(agents, id)).join(", ") : "every agent";
  return `${where} · ${who}`;
}

/** One subscription in the sheet. */
export function WebhookSheet({
  subscription,
  channels,
  agents,
  onChanged,
  onClose,
}: {
  subscription: WebhookSubscriptionListItem;
  channels: ChannelOption[];
  agents: AgentOption[];
  /** A write landed: re-read the list. */
  onChanged: () => void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  async function regenerate() {
    if (!window.confirm("Regenerate the signing secret? The old secret stops working immediately.")) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/webhook-subscriptions/${subscription.id}/regenerate-secret`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Regenerate failed (${res.status})`);
      const body = (await res.json()) as { signing_secret: string };
      setSecret(body.signing_secret);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Regenerate failed");
    } finally {
      setBusy(false);
    }
  }

  async function setEnabled(enabled: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/webhook-subscriptions/${subscription.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this webhook subscription? Pending deliveries will be discarded.")) return;
    setError(null);
    try {
      const res = await fetch(`/api/webhook-subscriptions/${subscription.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) throw new Error(`Delete failed (${res.status})`);
      onClose();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }

  return (
    <>
      <RecordHead
        recordKey="POST"
        title={subscription.name}
        onClose={onClose}
        more={
          <>
            <MenuItem onSelect={() => void navigator.clipboard?.writeText(subscription.target_url).catch(() => undefined)}>
              Copy target URL
            </MenuItem>
            <MenuItem onSelect={() => void remove()}>Delete this webhook</MenuItem>
          </>
        }
        meta={
          <>
            <span className={subscription.enabled ? "pill live" : "pill done"}>
              {subscription.enabled ? "active" : "paused"}
            </span>
            <span className="fact" style={{ fontSize: 11.5 }}>
              {subscription.target_url} · {subscription.deliveries_24h_count ?? 0} deliveries in 24h
            </span>
          </>
        }
      />
      <div className="sbody" data-testid={`webhook-sheet-${subscription.id}`}>
        {secret && (
          <div className="stopbox" data-testid="webhook-regenerate-secret-modal">
            <div className="h">
              <Stamp>once</Stamp>
              New signing secret. Save it now.
            </div>
            <p>The old secret stopped working immediately. This value is not shown again.</p>
            <div className="field">
              <label htmlFor="webhook-new-secret">Signing secret</label>
              <div className="in mono">
                <input
                  id="webhook-new-secret"
                  readOnly
                  value={secret}
                  onFocus={(event) => event.currentTarget.select()}
                  data-testid="webhook-regenerated-secret-value"
                />
              </div>
            </div>
            <span>
              <Btn size="sm" variant="secondary" onClick={() => setSecret(null)} data-testid="webhook-regenerated-secret-dismiss">
                I've stored this safely
              </Btn>
            </span>
          </div>
        )}
        {error && (
          <p role="alert" className="err-line">
            {error}
          </p>
        )}
        <div className="field">
          <label>Events</label>
          <div className="tags">
            {subscription.event_kinds.map((kind) => (
              <Chip key={kind}>{kind}</Chip>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Filter</label>
          <span className="fact" style={{ fontSize: 12 }}>
            {filterLine(subscription, channels, agents)}
          </span>
        </div>
        <div className="field">
          <label>Payload</label>
          <span className="fact" style={{ fontSize: 12 }}>
            {subscription.include_message_bodies ? "message bodies" : "no message bodies"} ·{" "}
            {subscription.include_attachments ? "attachment metadata" : "no attachments"}
          </span>
        </div>
        <div>
          <Fold title="Recent deliveries" count={subscription.deliveries_24h_count ?? undefined}>
            <DeliveryInspector subscription={subscription} />
          </Fold>
        </div>
      </div>
      <SheetFoot
        why={
          subscription.enabled ? (
            <>
              Regenerating the secret invalidates the old one <b>immediately</b>. Pausing holds new deliveries.
            </>
          ) : (
            <>Paused: nothing is delivered. Resuming sends events from now on.</>
          )
        }
        secondary={{ label: "Regenerate secret", onClick: () => void regenerate(), disabled: busy }}
        primary={{
          label: subscription.enabled ? "Pause" : "Resume",
          onClick: () => void setEnabled(!subscription.enabled),
          disabled: busy,
        }}
      />
    </>
  );
}

const NEW_WEBHOOK_FORM = "new-webhook-form";

function submitForm(id: string) {
  const form = document.getElementById(id);
  if (!(form instanceof HTMLFormElement)) return;
  if (typeof form.requestSubmit === "function") form.requestSubmit();
  else form.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
}

/** Create a subscription; the signing secret shows once, in this sheet. */
export function NewWebhookSheet({
  channels,
  agents,
  onCreated,
  onCancel,
}: {
  channels: ChannelOption[];
  agents: AgentOption[];
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [secretShown, setSecretShown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  return (
    <>
      <RecordHead
        recordKey="NEW WEBHOOK"
        title="Create webhook"
        onClose={onCancel}
        noExpand
        meta={
          <span className="fact" style={{ fontSize: 11.5 }}>
            One target, the event kinds you pick, signed with a secret shown once.
          </span>
        }
      />
      <div className="sbody">
        <SubscriptionForm
          formId={NEW_WEBHOOK_FORM}
          bare
          channels={channels}
          agents={agents}
          onCreated={onCreated}
          onCancel={onCancel}
          onSecretChange={setSecretShown}
          onSubmittingChange={setSubmitting}
        />
      </div>
      {secretShown ? (
        <SheetFoot
          why={
            <>
              The signing secret is shown once. <b>Store it before closing.</b>
            </>
          }
        />
      ) : (
        <SheetFoot
          why={
            <>
              The signing secret is shown once after creating. <b>Store it before closing.</b>
            </>
          }
          secondary={{ label: "Cancel", onClick: onCancel }}
          primary={{ label: "Create webhook", onClick: () => submitForm(NEW_WEBHOOK_FORM), disabled: submitting }}
        />
      )}
    </>
  );
}

export function WebhooksTab({ refreshKey }: ConnectionsTabProps) {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [agents, setAgents] = useState<AgentOption[]>([]);

  const reload = useCallback(async () => {
    try {
      const res = await fetch("/api/webhook-subscriptions", { credentials: "include" });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const body = (await res.json()) as { subscriptions: WebhookSubscriptionListItem[] };
      setLoad({ kind: "loaded", subscriptions: body.subscriptions });
    } catch (err) {
      setLoad({ kind: "error", error: err instanceof Error ? err.message : "Failed to load" });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey]);

  // Names for the filter, so the form and the sheet show them rather than ids.
  useEffect(() => {
    void fetch("/api/channels", { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return;
        const body = (await res.json()) as Array<{ id: string; name: string }>;
        if (Array.isArray(body)) setChannels(body.map((c) => ({ id: c.id, name: c.name ?? c.id })));
      })
      .catch(() => {
        // Best-effort: the form shows no channel filter without them.
      });
    void fetchWorkspaceAgentIdentity({ credentials: "include" })
      .then((identity) => {
        if (identity) setAgents([{ id: identity.id, name: identity.displayName }]);
      })
      .catch(() => {
        // Same fallback as above.
      });
  }, []);

  const subscriptions = load.kind === "loaded" ? load.subscriptions : [];
  const open = openId ? (subscriptions.find((sub) => sub.id === openId) ?? null) : null;
  const sheet = creating ? (
    <NewWebhookSheet
      channels={channels}
      agents={agents}
      onCreated={() => {
        setCreating(false);
        void reload();
      }}
      onCancel={() => setCreating(false)}
    />
  ) : open ? (
    <WebhookSheet
      key={open.id}
      subscription={open}
      channels={channels}
      agents={agents}
      onChanged={() => void reload()}
      onClose={() => setOpenId(null)}
    />
  ) : null;
  useOwnedSheet(sheet, () => {
    setCreating(false);
    setOpenId(null);
  });

  return (
    <Groups>
      {load.kind === "error" && (
        <div className="qnote err" role="alert">
          <span>{load.error}</span>
        </div>
      )}
      {load.kind === "loaded" && subscriptions.length === 0 ? (
        <div className="empty dashed tight" data-testid="webhook-empty-state">
          <p>No webhook subscriptions yet. Create one to receive event notifications at your endpoint.</p>
        </div>
      ) : (
        <Group label="Webhooks" plain empty={load.kind === "loading" ? "Reading the webhooks." : undefined}>
          {subscriptions.map((sub) => {
            const failing = sub.last_delivery_status === "failed" || sub.last_delivery_status === "dead";
            return (
              <Row
                key={sub.id}
                glyph={
                  <span className="cmark" style={MARK_STYLE} aria-hidden>
                    W
                  </span>
                }
                rowKey="POST"
                title={sub.name}
                tag={
                  !sub.enabled
                    ? { label: "paused", color: "var(--text-quiet)" }
                    : failing
                      ? { label: "failing", color: "var(--honesty)" }
                      : { label: "active", color: "var(--accent-active)" }
                }
                state={webhookStateLine(sub)}
                verb="Open"
                sel={!creating && sub.id === openId}
                onOpen={() => {
                  setCreating(false);
                  setOpenId(sub.id);
                }}
              />
            );
          })}
        </Group>
      )}
      <div className="qline">
        <span className="fact">Inbound webhooks from a connected tool are set on that connection.</span>
        <span className="acts">
          <Btn
            variant="secondary"
            size="sm"
            data-testid="webhook-add-button"
            onClick={() => {
              setOpenId(null);
              setCreating(true);
            }}
          >
            Create webhook
          </Btn>
        </span>
      </div>
    </Groups>
  );
}
